import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";
import { IMAGES } from "../data/content";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1 -mb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const STATS = [
    { value: "< 24 Std.", label: "Rückmeldung auf Ihre Anfrage" },
    { value: "Festpreis", label: "Verbindliches Angebot, keine Überraschungen" },
    { value: "LK Celle", label: "Regional verwurzelt & schnell vor Ort" },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section id="top" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-forest-deep">
            <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
                <img
                    src={IMAGES.hero}
                    alt="Perfekt gepflegte Außenanlage mit Rasen und Terrasse – Gartenpflege im Landkreis Celle"
                    className="w-full h-full object-cover"
                    loading="eager"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest-deep/35 to-forest-deep/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/50 via-transparent to-transparent" />

            <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-16 sm:pb-24 pt-40">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-1.5 mb-8"
                    data-testid="hero-region-badge"
                >
                    <MapPin size={13} className="text-leaf-soft" strokeWidth={2.4} />
                    <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/90">
                        Landkreis Celle · Reinigung — Pflege — Werterhalt
                    </span>
                </motion.div>

                <h1 className="font-display font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.06] max-w-3xl">
                    <MaskedLine delay={0.25}>Wir bringen Außenanlagen</MaskedLine>
                    <MaskedLine delay={0.38}>
                        wieder in <span className="text-leaf-soft">Bestform.</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                    className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl"
                    data-testid="hero-subline"
                >
                    Heckenschnitt, Pflasterreinigung, Rasenpflege und Laubentfernung –
                    professionelle Gartenpflege &amp; Außenreinigung für Privat- und Gewerbeflächen im Landkreis Celle.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                    className="mt-9 flex flex-col sm:flex-row gap-3.5"
                >
                    <button
                        data-testid="hero-cta-quote"
                        onClick={() => scrollToId("#angebot")}
                        className="group inline-flex items-center justify-center gap-2.5 bg-white text-forest-deep font-semibold px-7 py-4 rounded-full text-base transition-[transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_8px_40px_rgba(255,255,255,0.25)] active:scale-[0.98]"
                    >
                        Kostenloses Angebot anfragen
                        <ArrowRight size={17} strokeWidth={2.4} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                    <button
                        data-testid="hero-cta-before-after"
                        onClick={() => scrollToId("#vorher-nachher")}
                        className="inline-flex items-center justify-center gap-2.5 border border-white/35 text-white font-semibold px-7 py-4 rounded-full text-base backdrop-blur-sm transition-[background-color,border-color] duration-200 hover:bg-white/10 hover:border-white/60"
                    >
                        Vorher / Nachher ansehen
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.1 }}
                    className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl border-t border-white/15 pt-7"
                >
                    {STATS.map((s) => (
                        <div key={s.value} data-testid={`hero-stat-${s.value.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                            <div className="font-display font-bold text-xl text-white">{s.value}</div>
                            <div className="text-sm text-white/60 mt-1 leading-snug">{s.label}</div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
            >
                <ArrowDown size={14} className="text-white/50" />
                <span className="w-px h-10 bg-white/40 animate-scroll-hint" />
            </motion.div>
        </section>
    );
};
